import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import api from '../utils/api';

// Types for tracking stats
export interface TrackingOverview {
  period: { start: string; end: string };
  total_items: number;
  total_views: number;
  total_clicks: number;
  total_quote_requests: number;
  total_conversions: number;
  conversion_rate: number;
  avg_response_time: number;
  total_dealers_active: number;
  total_regions_active: number;
}

export interface StatsByStatus {
  status: string;
  items_count: number;
  total_views: number;
  unique_viewers: number;
  unique_dealers: number;
  total_clicks: number;
  quote_requests: number;
  quotes_accepted: number;
  conversion_rate: number;
  value_total: number;
  avg_price: number;
}

export interface GeoStats {
  by_region: {
    region: string;
    views: number;
    unique_dealers: number;
    unique_viewers: number;
    quote_requests: number;
    conversions: number;
    conversion_rate: number;
  }[];
  by_provincia: {
    region: string;
    provincia: string;
    views: number;
    dealers: number;
    conversions: number;
  }[];
  item_movements: {
    total_km: number;
    by_type: { type: string; count: number; total_km: number }[];
    by_region_from: { region: string; count: number }[];
    by_region_to: { region: string; count: number }[];
  };
}

export interface DealerLeaderboard {
  by_views: {
    rank: number;
    dealer_id: number;
    dealer_name: string;
    region: string;
    views: number;
    unique_items: number;
  }[];
  by_quote_requests: {
    rank: number;
    dealer_id: number;
    dealer_name: string;
    region: string;
    requests: number;
    accepted: number;
    conversion_rate: number;
  }[];
  by_conversions: {
    rank: number;
    dealer_id: number;
    dealer_name: string;
    region: string;
    sold_items: number;
    total_revenue: number;
    avg_sale_price: number;
  }[];
  by_activity: {
    rank: number;
    dealer_id: number;
    dealer_name: string;
    region: string;
    total_activity: number;
  }[];
  by_response_time: {
    rank: number;
    dealer_id: number;
    dealer_name: string;
    region: string;
    avg_response_hours: number;
    total_responses: number;
  }[];
}

export interface QuoteStats {
  overview: {
    total_requests: number;
    response_rate: number;
    avg_response_time_hours: number;
    acceptance_rate: number;
    rejection_rate: number;
    avg_offered_price: number;
    avg_accepted_price: number;
  };
  by_status: { status: string; count: number; percentage: number }[];
  by_region: { region: string; requests: number; accepted: number; rejection_rate: number }[];
  funnel: { stage: string; count: number; drop_off_percent: number }[];
}

export interface TemporalStats {
  hourly_distribution: { hour: number; views: number; clicks: number; quotes: number }[];
  daily_distribution: { day_of_week: string; views: number; clicks: number; quotes: number }[];
  weekly_trend: { week: string; views: number; clicks: number; quotes: number }[];
  peak_hours: { hour: number; activity: number }[];
  peak_days: { day_of_week: string; activity: number }[];
}

export interface TopActiveUser {
  user_id: number;
  user_name: string;
  user_email: string;
  region: string;
  user_type: string;
  activity_score: number;
  breakdown: {
    views_performed: number;
    items_viewed: number;
    clicks_made: number;
    quotes_requested: number;
    messages_sent: number;
    favorites_added: number;
    shares: number;
  };
  last_active: string;
  member_since: string;
}

// Hook for fetching tracking overview
export function useTrackingOverview(dateFrom?: string, dateTo?: string, enabled: boolean = true) {
  return useQuery<any>(
    ['tracking-overview', dateFrom, dateTo],
    async () => {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get('/stats/overview', { params });
      console.log('[useTrackingOverview] Response:', response.data);
      // Return the data directly
      return response.data?.success ? response.data.data : response.data;
    },
    {
      staleTime: 0, // Always fetch fresh data
      refetchOnWindowFocus: true,
      refetchInterval: 20000,
      enabled,
    }
  );
}

// Hook for fetching stats by status
export function useStatsByStatus(itemType: 'boat' | 'motor' = 'boat', dateFrom?: string, dateTo?: string, enabled: boolean = true) {
  return useQuery<StatsByStatus[]>(
    ['tracking-by-status', itemType, dateFrom, dateTo],
    async () => {
      const params: Record<string, string> = { item_type: itemType };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get('/stats/by-status', { params });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled,
    }
  );
}

// Hook for fetching geo stats
export function useGeoStats(itemType: 'boat' | 'motor' = 'boat', dateFrom?: string, dateTo?: string, enabled: boolean = true) {
  return useQuery<GeoStats>(
    ['tracking-geo', itemType, dateFrom, dateTo],
    async () => {
      const params: Record<string, string> = {};
      params.item_type = itemType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get('/stats/geo', { params });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled,
    }
  );
}

// Hook for fetching dealer leaderboard
export function useDealerLeaderboard(limit: number = 10, dateFrom?: string, dateTo?: string, enabled: boolean = true) {
  return useQuery<DealerLeaderboard>(
    ['tracking-leaderboard', limit, dateFrom, dateTo],
    async () => {
      const params: Record<string, string | number> = { limit };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get('/stats/leaderboard', { params });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled,
    }
  );
}

// Hook for fetching quote stats
export function useQuoteStats(dateFrom?: string, dateTo?: string) {
  return useQuery<QuoteStats>(
    ['tracking-quotes', dateFrom, dateTo],
    async () => {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get('/stats/quotes', { params });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}

// Hook for fetching temporal stats
export function useTemporalStats(dateFrom?: string, dateTo?: string) {
  return useQuery<TemporalStats>(
    ['tracking-temporal', dateFrom, dateTo],
    async () => {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get('/stats/temporal', { params });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}

// Hook for fetching top active user
export function useTopActiveUser(dateFrom?: string, dateTo?: string, enabled: boolean = true) {
  return useQuery<TopActiveUser | null>(
    ['tracking-top-user', dateFrom, dateTo],
    async () => {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get('/stats/top-user', { params });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled,
    }
  );
}

// Combined hook for all tracking stats
export function useAllTrackingStats(dateFrom?: string, dateTo?: string) {
  const queryClient = useQueryClient();
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');

  const overview = useTrackingOverview(dateFrom, dateTo);
  const byStatus = useStatsByStatus('boat', dateFrom, dateTo);
  const geo = useGeoStats('boat', dateFrom, dateTo);
  const leaderboard = useDealerLeaderboard(10, dateFrom, dateTo);
  const quotes = useQuoteStats(dateFrom, dateTo);
  const temporal = useTemporalStats(dateFrom, dateTo);
  const topUser = useTopActiveUser(dateFrom, dateTo);

  const refreshAll = useCallback(() => {
    queryClient.invalidateQueries(['tracking-']);
  }, [queryClient]);

  return {
    overview,
    byStatus,
    geo,
    leaderboard,
    quotes,
    temporal,
    topUser,
    selectedPeriod,
    setSelectedPeriod,
    refreshAll,
  };
}

// Hook for tracking events (to be used in components)
export function useTracking() {
  const queryClient = useQueryClient();

  const trackView = useCallback(async (data: {
    item_type: 'boat' | 'motor';
    item_id: number;
    referrer_type?: string;
    session_id?: string;
    view_duration?: number;
    viewer_region?: string;
    viewer_provincia?: string;
  }) => {
    const response = await api.post('/track/view', data);
    return response.data;
  }, []);

  const trackClick = useCallback(async (data: {
    item_type: 'boat' | 'motor';
    item_id: number;
    click_type: 'contact' | 'quote' | 'share' | 'favorite' | 'call' | 'map' | 'image' | 'video';
    session_id?: string;
    viewer_region?: string;
    viewer_provincia?: string;
  }) => {
    const response = await api.post('/track/click', data);
    return response.data;
  }, []);

  const trackQuote = useCallback(async (data: {
    item_type: 'boat' | 'motor';
    item_id: number;
    email: string;
    name?: string;
    phone?: string;
    message?: string;
    offered_price?: number;
    requester_region?: string;
    requester_provincia?: string;
  }) => {
    const response = await api.post('/track/quote', data);
    return response.data;
  }, []);

  const trackInteraction = useCallback(async (data: {
    to_dealer_id: number;
    interaction_type: 'view_boat' | 'view_motor' | 'request_quote' | 'chat_message' | 'phone_call' | 'share' | 'favorite';
    item_type?: 'boat' | 'motor';
    item_id?: number;
    message?: string;
  }) => {
    const response = await api.post('/track/interaction', data);
    return response.data;
  }, []);

  const trackMovement = useCallback(async (data: {
    item_type: 'boat' | 'motor';
    item_id: number;
    from_lat?: number;
    from_lng?: number;
    from_location?: string;
    to_lat?: number;
    to_lng?: number;
    to_location?: string;
    movement_type: 'delivery' | 'transfer' | 'sold' | 'test_drive';
    notes?: string;
  }) => {
    const response = await api.post('/track/movement', data);
    return response.data;
  }, []);

  return {
    trackView,
    trackClick,
    trackQuote,
    trackInteraction,
    trackMovement,
  };
}

// Dealer-specific tracking stats interface
export interface DealerTrackingStats {
  dealer_id: number;
  is_admin_view: boolean;
  viewing_own_data: boolean;
  total_items: number;
  total_boats: number;
  total_motors: number;
  available_items: number;
  reserved_items: number;
  sold_items: number;
  available_boats: number;
  reserved_boats: number;
  sold_boats: number;
  available_motors: number;
  reserved_motors: number;
  sold_motors: number;
  total_views: number;
  total_clicks: number;
  total_quote_requests: number;
  dealer_value: number;
  retail_value: number;
  boats_by_status: { status: string; count: string }[];
  motors_by_status: { status: string; count: string }[];
}

// Hook for fetching dealer tracking stats
export function useDealerTrackingStats(dateFrom?: string, dateTo?: string) {
  return useQuery<any>(
    ['dealer-tracking', dateFrom, dateTo],
    async () => {
      const params: Record<string, string> = {};
      if (dateFrom && dateFrom.length > 0) {
        params.date_from = dateFrom;
      }
      if (dateTo && dateTo.length > 0) {
        params.date_to = dateTo;
      }
      
      console.log('[useDealerTrackingStats] Fetching with params:', params);
      const response = await api.get('/stats/dealer', { params });
      console.log('[useDealerTrackingStats] Full response:', response);
      console.log('[useDealerTrackingStats] Response data:', response.data);
      // Return the data directly - if API returns {success: true, data: ...}, unwrap it
      return response.data?.success ? response.data.data : response.data;
    },
    {
      staleTime: 0, // Always fetch fresh data
      refetchOnWindowFocus: true,
      refetchInterval: 60000,
    }
  );
}
